// src/components/standard/ViewToggle.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Map, List } from 'lucide-react';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useSection } from '../../hooks/useSection';

interface ViewToggleProps {
  view: 'map' | 'list';
  onChange: (view: 'map' | 'list') => void;
  config: any;
  translations: Record<string, Record<string, string>>;
}

export const ViewToggle: React.FC<ViewToggleProps> = ({
  view,
  onChange,
  config,
  translations
}) => {
  const { i18n } = useTranslation();
  const section = useSection();
  
  // Initialize analytics with section context
  const analytics = useAnalytics({
    sectionId: section.id,
    sectionName: section.names.fr,
    sectionType: 'standard'
  });

  const getTranslatedText = (key: string): string => {
    const translation = translations[key];
    if (!translation) return key;
    return translation[i18n.language] || translation.fr || key;
  };

  const handleViewChange = (newView: 'map' | 'list') => {
    analytics.trackEvent('view', 'view_change', {
      previous_view: view,
      new_view: newView
    });
    
    onChange(newView);
  };

  return (
    <div 
      className="flex bg-white rounded-full overflow-hidden shadow-lg"
      style={{ padding: '4px' }}
    >
      <button
        onClick={() => handleViewChange('map')}
        className={`flex items-center px-6 py-2 rounded-full transition-all duration-200`}
        style={{ 
          backgroundColor: view === 'map' ? config['major-color'] : 'transparent',
          color: view === 'map' ? '#fff' : '#000'
        }}
        aria-pressed={view === 'map'}
        aria-label={getTranslatedText('view_map')}
      >
        <Map size={18} className="mr-2" />
        <span className="text-sm font-medium">{getTranslatedText('view_map')}</span>
      </button>
      <button
        onClick={() => handleViewChange('list')}
        className={`flex items-center px-6 py-2 rounded-full transition-all duration-200`}
        style={{ 
          backgroundColor: view === 'list' ? config['major-color'] : 'transparent',
          color: view === 'list' ? '#fff' : '#000'
        }}
        aria-pressed={view === 'list'}
        aria-label={getTranslatedText('view_list')}
      >
        <List size={18} className="mr-2" />
        <span className="text-sm font-medium">{getTranslatedText('view_list')}</span>
      </button>
    </div>
  );
};
